import React, {useState, useRef, useEffect} from 'react'
import { RichText } from 'prismic-reactjs'
import { getCursorFromDocumentIndex } from '@prismicio/gatsby-source-prismic-graphql';
import { graphql } from 'gatsby';
import { ThemeProvider } from "styled-components"
import get from 'lodash/get';

import { standardiseInternalURL, getPathFromMeta, getAlternateLanguagePaths, getCategoryPaginationPath, getBlogPath } from '../utils/link'

import Layout from '../components/layout'
import SEO from '../components/seo'

import StripCover from '../components/strip-cover'
import ComplexBody from '../components/complex-body'

import BlogArticles from '../components/blog-articles'
import BlogOther from '../components/blog-other'
import BlogSearchAndFilter from '../components/blog-search-and-filter'
import BlogPagination from '../components/blog-pagination'

import ConfigurationContext from '../context/configuration-context';
import PageMetaContext from '../context/page-meta-context';

import { THEME_WHITE, THEME_BLUE } from '../theme/theme'

export const query = graphql`
	query CategoryQuery($lang: String, $first: Int = 8, $after: String, $categoryId: String) {
		site {
			siteMetadata {
				siteUrl
				defaultLanguage
				includeDraftLanguages
			}
		}
		prismic{
			allConfigurations(lang:$lang){
				edges{
					node{
						_meta{
							lang
							uid
							type
						}

						flag
						language
						direction
						cyrillic
						
						site_name
						site_share_image
						site_title
						twitter_username

						label_author
						label_next
						label_previous
						label_of
						label_read_more
						label_view_all
						label_search
						placeholder_search
						headline_search
						label_search_results
						label_no_search_results
						label_show
						label_hide
						label_view_and_download
						label_archived_downloads

						months

						headline_404
						text_404
						headline_500
						text_500
					}
				}
			}
			allBlogs(lang:$lang){
				edges{
					node{
						_meta{
							lang
							uid
							type
							alternateLanguages {
								uid
								type
								lang
								id
							}
						}
						page_title
						page_description
						page_share_image
						name
						body{
							... on PRISMIC_BlogBodyPromo {
								type
								label
								primary {
									heading
									background
									background_color
									label
									links
								}
							}
							... on PRISMIC_BlogBodyBanner {
								type
								label
								primary {
									heading
									text
									links
									color
									image
								}
							}
						}
					}
				}
			}
			allCategorys(lang:$lang, id:$categoryId) {
				edges{
					node{
						_meta{
							lang
							uid
							type
							alternateLanguages {
								uid
								type
								lang
								id
							}
						}
						name
						page_title
						page_description
						page_share_image
					}
				}
			}
			allArticles(sortBy: meta_firstPublicationDate_DESC, lang:$lang, first: $first, after: $after, where: {category: $categoryId}) {
				pageInfo {
					hasNextPage
					hasPreviousPage
					startCursor
					endCursor
				}
				edges {
					node {
						_meta {
							uid
							lang
							type
							firstPublicationDate
						}
						headline
						introduction
						cover_image
					}
				}
			}
			
		}
	}
`

const CategoryPage = ({ data, location, pageContext, prismic }) => {
	const site = get(data,"site.siteMetadata");
	const page = get(data,"prismic.allCategorys.edges[0].node");
	const configuration = get(data,"prismic.allConfigurations.edges[0].node");
	const blog = get(data,"prismic.allBlogs.edges[0].node");
	
	const articles = get(data,"prismic.allArticles.edges",[]).map(edge => edge.node);
	const categories = get(data,"prismic.allCategorys.edges",[]).map(edge => edge.node);

	// check all relevant content exists
	if(!page || !configuration || !site || !blog) return null;

	// check actual path matches expected path
	// if(standardiseInternalURL(location.pathname) !== getPathFromMeta(page._meta, pageContext.page)) return null;

	return(
		<ConfigurationContext.Provider value={configuration}>
			<PageMetaContext.Provider value={page._meta}>
				<ThemeProvider theme={{...THEME_WHITE,direction:configuration.direction,language:page._meta.lang,cyrillic:configuration.cyrillic}}>
					<Layout language={page._meta.lang} alternateLanguages={page._meta.alternateLanguages} meta={page._meta}>
						<SEO 
							includeDraftLanguages={site.includeDraftLanguages}
							siteURL={site.siteUrl}
							siteDirection={configuration.direction}
							siteName={configuration.site_name}
							siteTitle={configuration.site_title}
							siteShareImage={configuration.site_share_image}
							
							language={page._meta.lang}
							alternateLanguages={getAlternateLanguagePaths(page._meta.alternateLanguages)}

							twitterUsername={configuration.twitter_username} 
							
							pagePath={getPathFromMeta(page._meta)}
							pageSlug={page._meta.uid}
							pageType={page._meta.type}
							pageTitle={page.page_title}
							pageDescription={page.page_description}
							pageShareImage={page.page_share_image}
						/>
						<StripCover labelPath={getBlogPath(configuration._meta.lang)} color='Blue' label={blog.name} heading={pageContext.categoryName} />
						<BlogArticles articles={articles} />
						{pageContext.totalPages > 1 && <BlogPagination previous={getPathFromMeta(page._meta,pageContext.page - 1)} next={getPathFromMeta(page._meta,pageContext.page + 1)} page={pageContext.page} totalPages={pageContext.totalPages} />}
						{(blog.body.length > 1) && <BlogOther item={blog.body[blog.body.length - 1]} condenseTop={true} />}
					</Layout>
				</ThemeProvider>
			</PageMetaContext.Provider>
		</ConfigurationContext.Provider>
	)
}

CategoryPage.query = query;

export default CategoryPage;
